import React from "react";
import PanelShell from "../../shared/PanelShell";
import { SkeletonText } from "../../shared/Skeleton";
import { fmtNumber, labelFor } from "../../shared/format";
import { ROW_LIST_BLEED } from "./rowStyles";

// chat_questions — the thesis panel. Every question a visitor typed is
// something the website failed to answer, so this is a content audit written by
// real guests. Questions render VERBATIM, ranked by frequency; topics[] sit
// beside them as ranked horizontal bars, and the bars are clickable and filter
// the list.
//
// The bars are markup, not a Recharts chart. A Recharts <Bar> cell is an SVG
// rect: it takes a click but is not in the tab order, carries no accessible
// name, and cannot be given the 44px touch box the spec's floor requires. A row
// of <button>s is keyboard-reachable, screen-readable and hit-testable for free,
// and the mark is a proportional fill rather than a hue, so it survives
// greyscale and print.
//
// FILTERING: neither topics[] nor questions[] carries conversation ids, and the
// topic vocabulary ("dump_station", "pricing") matches no stored column on the
// list rows — `primary_intent` is free text. So a bar raises a TEXT filter,
// which the page applies through its existing conversation search. That is an
// honest narrowing of the list by the words the topic is about; it is not a
// claim that the RPC and the list agree on a key, because they do not.
// DISPLAY CAPS. §2.4 specifies three: "Follow-ups show five, website gaps show
// five, topics show eight." Only `TOPICS_SHOWN` is that spec's to set —
// `QUESTIONS_SHOWN` is ours, and the test that guards these says which is which
// so nobody "corrects" the unspecified one to match.
const QUESTIONS_SHOWN = 12;
const TOPICS_SHOWN = 8;

// EXACT IDS, not word matching. fn_get_conversation_topics returns
// conversation_ids per topic as of 2026-08-27; before that a bar could only
// match its own words against each row's summary text, and three of the top ten
// topics matched nothing at all — `greeting` was the most prominent bar on the
// panel and emptied the list, because summaries say "greeted". A bar that says
// "13" and yields zero rows contradicts itself on the same screen.
const TopicBar = ({ topic, count, max, pct, conversationIds, onFilter, onSelect, row, active }) => {
  // Width is a proportion of the largest bar in the set — a drawing instruction,
  // not a metric: nothing derived here is ever shown as a number.
  const width = max > 0 ? Math.max(4, Math.round((count / max) * 100)) : 0;
  const label = labelFor(topic);
  return (
    <button
      type="button"
      onClick={() => (onSelect
        // THE DRILL PATH. `onSelect` raises the rail filter AND opens the level
        // in one call — see useDrill for why those are not two things a caller
        // remembers to do in order. Before it, a topic was the one drillable
        // thing on the page that narrowed the list without saying what to.
        // The HUMANISED label travels with the row. `topic` is a stored key
        // (`dump_station`), and before this the level title — and therefore the
        // slide-over heading and the rail's chip — read it verbatim. The word
        // fallback below always used `label`, so the two paths disagreed about
        // what the same bar was called.
        ? onSelect({ ...row, label })
        : onFilter(
          conversationIds?.length
            ? { source: "chat_questions", kind: "conversations", value: conversationIds, label }
            // Fall back to the words only when the backend sent no ids. Keeps
            // the bar working against an older payload rather than going inert.
            : { source: "chat_questions", kind: "topic", value: topic, label },
        ))}
      aria-pressed={active}
      className={`w-full text-left min-h-[44px] px-2 py-1.5 rounded-md transition-colors ${
        active ? "bg-[#EAF6FF] dark:bg-blue-900/30" : "hover:bg-gray-50 dark:hover:bg-gray-800/60"
      }`}
      title={`Show conversations mentioning ${label}`}
    >
      <span className="flex items-baseline justify-between gap-2">
        <span className="gi-body text-gray-900 dark:text-gray-100 truncate">{label}</span>
        <span className="gi-label text-gray-500 dark:text-gray-400 tabular-nums shrink-0">
          {fmtNumber(count)}
          {/* "Below ten people show the raw count, never a percentage" (§G.2): a
              percentage of a handful of conversations is misleading, so the pct is
              suppressed under ten and the raw count carries the row alone. */}
          {pct != null && Number(count) >= 10 && <span className="ml-1">({fmtNumber(pct, true)}%)</span>}
        </span>
      </span>
      <span className="mt-1 block h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
        <span className="block h-full rounded-full bg-[#2099DF] dark:bg-[#32B1FF]" style={{ width: `${width}%` }} />
      </span>
    </button>
  );
};

const ChatQuestionsPanel = ({ say, topics, loading, onFilter, activeFilter, onTopic }) => {
  const questions = Array.isArray(topics?.questions) ? topics.questions : [];
  const topicRows = Array.isArray(topics?.topics) ? topics.topics : [];
  const shownQuestions = questions.slice(0, QUESTIONS_SHOWN);
  const shownTopics = topicRows.slice(0, TOPICS_SHOWN);
  const max = shownTopics.reduce((m, t) => Math.max(m, Number(t.count ?? 0)), 0);
  const raise = onFilter || (() => {});

  return (
    <PanelShell
      panelKey="chat_questions"
      label="What visitors asked"
      say={say}
      hasData={questions.length > 0 || topicRows.length > 0}
      loading={loading}
    >
      {loading && !topics ? <SkeletonText lines={6} /> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Questions verbatim, ranked by frequency. No paraphrase, no
              truncation — the wording IS the finding. */}
          <div>
            <h4 className="gi-label text-gray-500 dark:text-gray-400 mb-2">Questions, most asked first</h4>
            <ul className={`divide-y divide-gray-200 dark:divide-gray-800 ${ROW_LIST_BLEED}`}>
              {shownQuestions.map((q, i) => (
                <li key={i} className="py-2">
                  {/* Display-only. The questions carry no conversation ids and the
                      page passes no onFilter here, so a row was a control that did
                      nothing on click — no hover affordance, no tooltip. */}
                  <div className="w-full text-left flex items-start justify-between gap-3">
                    <span className="gi-body text-gray-800 dark:text-gray-200 break-words">{q.question}</span>
                    {q.count != null && (
                      <span className="gi-label text-gray-500 dark:text-gray-400 tabular-nums shrink-0">{fmtNumber(q.count)}×</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Topics as ranked bars. Clicking one filters the list. */}
          <div>
            <h4 className="gi-label text-gray-500 dark:text-gray-400 mb-2">Topics — select one to filter the list</h4>
            <div className={`space-y-1 ${ROW_LIST_BLEED}`}>
              {shownTopics.map((t, i) => (
                <TopicBar
                  key={t.topic ?? i}
                  topic={t.topic}
                  count={Number(t.count ?? 0)}
                  pct={t.pct_of_conversations}
                  max={max}
                  conversationIds={t.conversation_ids}
                  onFilter={raise}
                  onSelect={onTopic}
                  row={t}
                  // Active state has to follow whichever shape the bar raised, or
                  // the pressed bar and the chip disagree about what is filtered.
                  active={
                    (activeFilter?.kind === "topic" && activeFilter?.value === t.topic)
                    || (activeFilter?.kind === "conversations"
                        && activeFilter?.source === "chat_questions"
                        && activeFilter?.label === labelFor(t.topic))
                  }
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </PanelShell>
  );
};

export default ChatQuestionsPanel;
